import { useState, useEffect } from 'react'
import {
  Server,
  Database,
  Activity,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  TrendingUp,
  Clock,
  HardDrive,
  Zap,
  BarChart3
} from 'lucide-react'

interface HealthMetrics {
  blobs: {
    status: string
    totalStores: number
    healthyStores: number
    totalBlobs: number
    avgListLatency: number
    stores: Array<{
      name: string
      status: string
      blobCount?: number
      listLatency?: number
      readLatency?: number | null
      error?: string
      lastChecked: string
    }>
  }
  functions: {
    status: string
    lastHour: {
      totalInvocations: number
      successfulInvocations: number
      failedInvocations: number
      errorRate: number
      avgDuration: number
    }
    endpoints: Record<string, number>
    error?: string
  }
  audit: {
    status: string
    totalLogs: number
    last7Days: number
    oldLogsNeedingCleanup: number
    logsByDate: Record<string, number>
    error?: string
  }
  performance: {
    status: string
    sampleSize?: number
    latency?: {
      avg: number
      p50: number
      p95: number
      p99: number
      min: number
      max: number
    }
    message?: string
    error?: string
  }
}

interface HealthResponse {
  health: HealthMetrics
  timestamp: string
  status: 'healthy' | 'degraded' | 'error'
}

const SystemHealthMonitor = () => {
  const [health, setHealth] = useState<HealthResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [autoRefresh, setAutoRefresh] = useState(false)

  useEffect(() => {
    loadHealth()
  }, [])

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(loadHealth, 30000) // Refresh every 30s
      return () => clearInterval(interval)
    }
  }, [autoRefresh])

  const loadHealth = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/platform-admin/health', {
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to load health metrics')
      }

      const data = await response.json()
      setHealth(data)
    } catch (err) {
      console.error('Health check error:', err)
      setError(err instanceof Error ? err.message : 'Failed to load health metrics')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-100 border-green-200'
      case 'degraded':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200'
      case 'error':
        return 'text-red-600 bg-red-100 border-red-200'
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-600" />
      default:
        return <Activity className="w-5 h-5 text-gray-600" />
    }
  }

  if (loading && !health) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading system health...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <div>
            <h3 className="font-semibold text-red-900">Error Loading Health Metrics</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
        <button
          onClick={loadHealth}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    )
  }

  if (!health) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Server className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">System Health</h2>
            <p className="text-sm text-gray-600">Infrastructure monitoring and performance</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={loadHealth}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Overall Status */}
      <div className={`rounded-lg border p-6 ${getStatusColor(health.status)}`}>
        <div className="flex items-center gap-3">
          {getStatusIcon(health.status)}
          <div className="flex-1">
            <h3 className="text-lg font-semibold capitalize">System Status: {health.status}</h3>
            <p className="text-sm mt-1">
              Last checked: {new Date(health.timestamp).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Blobs Health */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Database className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Netlify Blobs</h3>
            <p className="text-sm text-gray-600">Storage health and performance</p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(health.health.blobs.status)}`}>
            {health.health.blobs.status}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Stores</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {health.health.blobs.totalStores}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Healthy Stores</p>
            <p className="text-2xl font-bold text-green-600 mt-1">
              {health.health.blobs.healthyStores}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Total Blobs</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {health.health.blobs.totalBlobs.toLocaleString()}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Avg List Latency</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {health.health.blobs.avgListLatency}ms
            </p>
          </div>
        </div>

        {/* Individual Store Health */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Store Details</h4>
          {health.health.blobs.stores.map((store) => (
            <div
              key={store.name}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-3">
                {getStatusIcon(store.status)}
                <div>
                  <p className="font-medium text-gray-900">{store.name}</p>
                  {store.error && (
                    <p className="text-xs text-red-600 mt-1">{store.error}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-600">
                {store.blobCount !== undefined && (
                  <div>
                    <HardDrive className="w-3 h-3 inline mr-1" />
                    {store.blobCount} blobs
                  </div>
                )}
                {store.listLatency !== undefined && (
                  <div>
                    <Clock className="w-3 h-3 inline mr-1" />
                    List: {store.listLatency}ms
                  </div>
                )}
                {store.readLatency && (
                  <div>
                    <Clock className="w-3 h-3 inline mr-1" />
                    Read: {store.readLatency}ms
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Functions Health */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Zap className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Netlify Functions</h3>
            <p className="text-sm text-gray-600">Serverless function performance</p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(health.health.functions.status)}`}>
            {health.health.functions.status}
          </div>
        </div>

        {health.health.functions.error ? (
          <div className="text-sm text-red-600">{health.health.functions.error}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Invocations</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {health.health.functions.lastHour.totalInvocations}
                </p>
                <p className="text-xs text-gray-500 mt-1">Last hour</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Successful</p>
                <p className="text-2xl font-bold text-green-600 mt-1">
                  {health.health.functions.lastHour.successfulInvocations}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {health.health.functions.lastHour.failedInvocations}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Error Rate</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {health.health.functions.lastHour.errorRate}%
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Avg Duration</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {health.health.functions.lastHour.avgDuration}ms
                </p>
              </div>
            </div>

            {/* Endpoint Activity */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Endpoint Activity (Last Hour)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(health.health.functions.endpoints).map(([name, count]) => (
                  <div key={name} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600 capitalize">{name}</p>
                    <p className="text-lg font-semibold text-gray-900 mt-1">{count}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-orange-100 rounded-lg">
            <BarChart3 className="w-5 h-5 text-orange-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Performance Metrics</h3>
            <p className="text-sm text-gray-600">Latency percentiles</p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(health.health.performance.status)}`}>
            {health.health.performance.status}
          </div>
        </div>

        {health.health.performance.error ? (
          <div className="text-sm text-red-600">{health.health.performance.error}</div>
        ) : health.health.performance.message ? (
          <div className="text-sm text-gray-600">{health.health.performance.message}</div>
        ) : health.health.performance.latency ? (
          <>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Sample size: {health.health.performance.sampleSize?.toLocaleString()} requests
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Min</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {health.health.performance.latency.min}ms
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Avg</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {health.health.performance.latency.avg}ms
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">p50</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {health.health.performance.latency.p50}ms
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">p95</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">
                  {health.health.performance.latency.p95}ms
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">p99</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">
                  {health.health.performance.latency.p99}ms
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Max</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {health.health.performance.latency.max}ms
                </p>
              </div>
            </div>
          </>
        ) : null}
      </div>

      {/* Audit System Health */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Activity className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">Audit System</h3>
            <p className="text-sm text-gray-600">Logging system health</p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(health.health.audit.status)}`}>
            {health.health.audit.status}
          </div>
        </div>

        {health.health.audit.error ? (
          <div className="text-sm text-red-600">{health.health.audit.error}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Logs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {health.health.audit.totalLogs.toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Last 7 Days</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {health.health.audit.last7Days.toLocaleString()}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">Old Logs (Cleanup Needed)</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">
                  {health.health.audit.oldLogsNeedingCleanup}
                </p>
              </div>
            </div>

            {/* Recent Activity Chart */}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Activity by Date</h4>
              <div className="space-y-2">
                {Object.entries(health.health.audit.logsByDate).map(([date, count]) => {
                  const maxCount = Math.max(...Object.values(health.health.audit.logsByDate), 1)
                  const widthPercentage = maxCount > 0 ? Math.min((count / maxCount) * 100, 100) : 0
                  return (
                    <div key={date} className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 w-24">{date}</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-6 relative">
                        <div
                          className="bg-indigo-600 h-6 rounded-full flex items-center justify-end pr-2"
                          style={{
                            width: `${widthPercentage}%`
                          }}
                        >
                          <span className="text-xs text-white font-medium">{count}</span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default SystemHealthMonitor
